//External Libraries
import { useState } from 'react';
import { useTranslation } from 'react-i18next';

//Utils
import { REPLIES_COLLAPSE_THRESHOLD } from '../utils';

//Components
import { CMButton } from '@chartmetric/chartmetric-design-system/components/CMButton';
import { CMFlex } from '@chartmetric/chartmetric-design-system/components/CMFlex';
import CommentItem from './CommentItem';

//Types
import type { AnnouncementCommentEdit, CommentTreeNode } from '../types';

interface CommentThreadProps {
  node: CommentTreeNode;
  currentUserId: number | null;
  editingCommentId: number | null;
  canSetDisplayName: boolean;
  onEdit: (commentId: number, edit: AnnouncementCommentEdit) => Promise<boolean>;
  onDelete: (commentId: number) => void;
  onReply: (commentId: number, userName: string | null) => void;
  parentUserName?: string;
}

function CommentThread({
  node,
  currentUserId,
  editingCommentId,
  canSetDisplayName,
  onEdit,
  onDelete,
  onReply,
  parentUserName,
}: CommentThreadProps) {
  const { t } = useTranslation('common', { keyPrefix: 'announcementPanel' });
  const [showAllReplies, setShowAllReplies] = useState(false);

  const visibleReplies =
    showAllReplies || node.replies.length <= REPLIES_COLLAPSE_THRESHOLD
      ? node.replies
      : node.replies.slice(0, REPLIES_COLLAPSE_THRESHOLD);
  const hiddenCount = node.replies.length - visibleReplies.length;

  return (
    <CMFlex vertical fullWidth gap="sm">
      <CommentItem
        comment={node}
        isOwn={currentUserId !== null && currentUserId === node.user_id}
        isEditing={editingCommentId === node.id}
        canSetDisplayName={canSetDisplayName}
        onEdit={onEdit}
        onDelete={onDelete}
        onReply={onReply}
        parentUserName={parentUserName}
      />
      {node.replies.length > 0 && (
        <CMFlex vertical pl="lg" gap="sm" fullWidth borderSide="left" borderColor="lightGray">
          {visibleReplies.map((reply) => (
            <CommentThread
              key={reply.id}
              node={reply}
              currentUserId={currentUserId}
              editingCommentId={editingCommentId}
              canSetDisplayName={canSetDisplayName}
              onEdit={onEdit}
              onDelete={onDelete}
              onReply={onReply}
              parentUserName={node.deleted_at !== null ? undefined : (node.user_name ?? undefined)}
            />
          ))}
          {hiddenCount > 0 && (
            <CMButton
              text={t('comments.showMoreReplies', { count: hiddenCount })}
              variant="secondaryText"
              size="xs"
              onClick={() => setShowAllReplies(true)}
            />
          )}
        </CMFlex>
      )}
    </CMFlex>
  );
}

export default CommentThread;
